import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, IconButton, Link, Typography } from '@mui/material';
import { CloseRounded, LocalShippingOutlined } from '@mui/icons-material';
import { useNetwork } from '../../../utils/network';
import { PT } from '../../../components/portalUi';
import type { Parcel } from './CardAuction';

/* A card-break winner with postage still unpaid sees this above every portal page (not the
 * arena, not the Incrypt Break page itself — that page shows the parcels): the cards are packed
 * and waiting for an address. Read once per portal load; closing it lasts for this sign-in
 * (sessionStorage), and a new unpaid parcel brings it back. */
const KEY = 'inc.cardParcelNotice.dismissed';

export const CardParcelNotice = () => {
  const { get } = useNetwork();
  const location = useLocation();
  const [unpaid, setUnpaid] = useState<Parcel[]>([]);
  const [dismissed, setDismissed] = useState<string>(() => {
    try { return sessionStorage.getItem(KEY) || ''; } catch { return ''; }
  });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = (await get('/card-auctions/me/shipments', true)) as { shipments: Parcel[] };
        if (alive) setUnpaid((r.shipments || []).filter((p) => p.status === 'awaiting_address' || p.status === 'quoted'));
      } catch { /* no parcels, or not signed in — nothing to say */ }
    })();
    return () => { alive = false; };
    // once per portal load; the Incrypt Break page re-reads on its own after a payment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!unpaid.length || location.pathname.endsWith('/incrypt-break')) return null;
  const sig = unpaid.map((p) => p.id).sort().join(',');
  if (dismissed === sig) return null;
  const dismiss = () => {
    try { sessionStorage.setItem(KEY, sig); } catch { /* fine */ }
    setDismissed(sig);
  };
  const copies = unpaid.reduce((n, p) => n + p.copies, 0);
  const one = unpaid.length === 1 ? unpaid[0] : null;

  return (
    <Box
      role="status"
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mx: 2, mt: 2, mb: 1, p: { xs: 1.5, sm: 2 }, borderRadius: '14px', border: '1px solid rgba(232,200,119,0.5)', background: 'rgba(232,200,119,0.08)' }}
    >
      <LocalShippingOutlined sx={{ color: '#e8c877', flexShrink: 0, mt: '1px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: PT.text, lineHeight: 1.45 }}>
          {copies} card{copies === 1 ? '' : 's'} you won{one && one.auctionTitle ? ` from ${one.auctionTitle}` : ' from the Incrypt Break'} {copies === 1 ? 'is' : 'are'} packed and waiting. Enter your delivery address and pay the postage to have {copies === 1 ? 'it' : 'them'} sent.
          {one && one.payBy ? ` Please pay by ${new Date(one.payBy).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}.` : ''}
        </Typography>
        <Link component={RouterLink} to="/member-portal/incrypt-break" sx={{ display: 'inline-block', mt: 0.5, fontSize: 14, fontWeight: 700, color: PT.teal, textDecorationColor: 'rgba(56,232,255,0.5)' }}>
          Enter my address
        </Link>
      </Box>
      <IconButton onClick={dismiss} aria-label="Dismiss" size="small" sx={{ color: PT.textMuted, mt: '-4px', mr: '-4px', '&:hover': { color: PT.text } }}>
        <CloseRounded fontSize="small" />
      </IconButton>
    </Box>
  );
};
